import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CommonService } from '@fuse/services/common.service';
import { RETHROW_HTTP_ERRORS } from '@fuse/services/interceptor.service';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { environment } from 'environments/environment';
import { GetSettingsData, SaveSettingsRequest, SettingsData, SmtpApiError, SmtpEnvelope, VerifyData } from './models';

const GENERIC_ERROR = 'Something went wrong. Please try again.';

export interface SmtpResult<T> {
    data: T;
    message: string;
}

/**
 * Unwraps the `{status, message, data}` envelope. The app's HTTP interceptor turns
 * HTTP errors into normal values, so an HttpErrorResponse can arrive here as well.
 * `data` is kept on the error because 400 (verify) and 429 replies carry details in it.
 */
export function unwrapSmtpEnvelope<T>(response: SmtpEnvelope<T> | HttpErrorResponse | null): T {
    if (response instanceof HttpErrorResponse) {
        throw new SmtpApiError(response.status || 0, response.error?.message || GENERIC_ERROR, response.error?.data ?? null);
    }
    if (!response || typeof response.status !== 'number') {
        throw new SmtpApiError(0, GENERIC_ERROR);
    }
    if (response.status !== 200) {
        throw new SmtpApiError(response.status, response.message || GENERIC_ERROR, response.data ?? null);
    }
    return response.data;
}

@Injectable({
    providedIn: 'root'
})
export class SmtpApiService {

    private readonly baseUrl = environment.apiURL + 'admin/smtp/';

    constructor(
        private _httpClient: HttpClient,
        private _commonService: CommonService,
        private _fuseProgressBarService: FuseProgressBarService,
    ) { }

    getSettings(): Promise<SmtpResult<GetSettingsData>> {
        return this.request(this._httpClient.get<SmtpEnvelope<GetSettingsData>>(this.baseUrl + 'getSettings', this.options()));
    }

    saveSettings(body: SaveSettingsRequest): Promise<SmtpResult<SettingsData>> {
        return this.request(this._httpClient.post<SmtpEnvelope<SettingsData>>(this.baseUrl + 'saveSettings', body, this.options()));
    }

    /** Opens a real connection with the saved settings. A failed check rejects with status 400. */
    verifySettings(): Promise<SmtpResult<VerifyData>> {
        return this.request(this._httpClient.post<SmtpEnvelope<VerifyData>>(this.baseUrl + 'verifySettings', {}, this.options()));
    }

    setEnabled(enabled: boolean): Promise<SmtpResult<SettingsData>> {
        return this.request(this._httpClient.post<SmtpEnvelope<SettingsData>>(this.baseUrl + 'setEnabled', { enabled }, this.options()));
    }

    deleteSettings(): Promise<SmtpResult<SettingsData>> {
        return this.request(this._httpClient.post<SmtpEnvelope<SettingsData>>(this.baseUrl + 'deleteSettings', {}, this.options()));
    }

    /** Auth header, plus real HTTP errors (e.g. an expired-session 401) delivered to us instead of dropped. */
    private options() {
        return { ...this._commonService.getHeader(), context: new HttpContext().set(RETHROW_HTTP_ERRORS, true) };
    }

    private request<T>(call: Observable<SmtpEnvelope<T>>): Promise<SmtpResult<T>> {
        this._fuseProgressBarService.show();
        return call.toPromise()
            .then(response => ({ data: unwrapSmtpEnvelope<T>(response), message: response.message }), error => ({ data: unwrapSmtpEnvelope<T>(error), message: '' }))
            .finally(() => this._fuseProgressBarService.hide());
    }
}
